import $ from 'jquery';

const TOGGLE_SELECTOR = '.menu__toggle';
const DESKTOP_MQ_FALLBACK = '(min-width: 1400px)';
const EVENT_NS = '.dropdownMenu';
const KEYBOARD_NAV_KEYS = {
    Tab: true,
    ArrowLeft: true,
    ArrowRight: true,
    ArrowUp: true,
    ArrowDown: true,
    Home: true,
    End: true,
};

const desktopMq = () => {
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue('--breakpoint-xxl')
        .trim();

    return value ? `(min-width: ${value})` : DESKTOP_MQ_FALLBACK;
};

const isDesktop = () => window.matchMedia(desktopMq()).matches;

const canHoverOpen = () =>
    isDesktop() && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

const HOVER_DELAY_MS = 120;

let hoverTimer = null;
let pointerActive = true;

const clearHoverTimer = () => {
    if (hoverTimer !== null) {
        window.clearTimeout(hoverTimer);
        hoverTimer = null;
    }
};

const scheduleHover = (fn) => {
    clearHoverTimer();
    hoverTimer = window.setTimeout(() => {
        hoverTimer = null;
        fn();
    }, HOVER_DELAY_MS);
};

const getSubmenu = ($toggle) => {
    const id = $toggle.attr('aria-controls');
    if (!id) {
        return $();
    }

    const safeId = window.CSS && CSS.escape ? CSS.escape(id) : id;
    return $(`#${safeId}`);
};

const isOpen = ($toggle) => $toggle.attr('aria-expanded') === 'true';

const EDGE = 16;

const resetSubmenuPosition = ($submenu) => {
    $submenu.css({ left: '', right: '', maxHeight: '' });
};

const fitSubmenu = ($submenu) => {
    resetSubmenuPosition($submenu);

    if (!isDesktop()) {
        return;
    }

    const el = $submenu[0];
    const rect = el.getBoundingClientRect();

    if (rect.right > window.innerWidth - EDGE) {
        $submenu.css({ left: 'auto', right: 0 });

        const flipped = el.getBoundingClientRect();
        if (flipped.left < EDGE) {
            const parent = el.offsetParent;
            if (parent) {
                const parentLeft = parent.getBoundingClientRect().left;
                $submenu.css({
                    left: `${EDGE - parentLeft}px`,
                    right: 'auto',
                });
            }
        }
    }

    const top = el.getBoundingClientRect().top;
    const available = window.innerHeight - top - EDGE;
    $submenu.css({
        maxHeight: `${Math.max(available, 120)}px`,
    });
};

const openToggle = ($toggle) => {
    const $submenu = getSubmenu($toggle);
    if (!$submenu.length) {
        return;
    }

    $toggle.attr('aria-expanded', 'true');
    $submenu.prop('hidden', false);
    fitSubmenu($submenu);
};

const closeToggle = ($toggle) => {
    const $submenu = getSubmenu($toggle);
    if (!$submenu.length) {
        return;
    }

    $toggle.attr('aria-expanded', 'false');
    $submenu.prop('hidden', true);
    resetSubmenuPosition($submenu);
    $toggle.closest('li.menu__item--has-children').removeData('hoverLocked');
};

const getToggleForLi = ($li) => {
    const $separate = $li.children(TOGGLE_SELECTOR);
    if ($separate.length) {
        return $separate.first();
    }

    const $any = $li.find(TOGGLE_SELECTOR);
    return $any.length ? $any.first() : $();
};

const closeSiblings = ($toggle) => {
    if (!isDesktop()) {
        return;
    }

    const $li = $toggle.closest('li');
    $li.siblings('li').each(function closeSibling() {
        const $sibToggle = getToggleForLi($(this));
        if ($sibToggle.length) {
            closeToggle($sibToggle);
        }
    });
};

const closeAll = ($menu) => {
    $menu.find(TOGGLE_SELECTOR).each(function closeEach() {
        closeToggle($(this));
    });
};

const isFocusInside = ($el) => {
    const active = document.activeElement;
    return Boolean(active && $el.length && ($el[0] === active || $.contains($el[0], active)));
};

const shouldKeepOpenForKeyboard = ($li) => !pointerActive && isFocusInside($li);

const otherKeyboardOpenHasFocus = ($li) => {
    if (pointerActive) {
        return false;
    }

    const $focusedItem = $(document.activeElement).closest('li.menu__item--has-children');
    if (!$focusedItem.length || $focusedItem[0] === $li[0]) {
        return false;
    }

    const $toggle = getToggleForLi($focusedItem);
    return $toggle.length && isOpen($toggle);
};

const initDropdownMenu = () => {
    const $menu = $('.menu');
    if (!$menu.length) {
        return;
    }

    clearHoverTimer();
    $menu.off(EVENT_NS);
    $(document).off(EVENT_NS);
    $(window).off(EVENT_NS);

    $menu.find(TOGGLE_SELECTOR).each(function syncHidden() {
        const $toggle = $(this);
        const $submenu = getSubmenu($toggle);
        if (!$submenu.length) {
            return;
        }

        $submenu.prop('hidden', !isOpen($toggle));
    });

    $menu.on(`click${EVENT_NS}`, TOGGLE_SELECTOR, function onToggle(e) {
        e.preventDefault();
        clearHoverTimer();

        const $toggle = $(this);
        const $li = $toggle.closest('li.menu__item--has-children');
        closeSiblings($toggle);

        if (isOpen($toggle)) {
            closeToggle($toggle);
            if (canHoverOpen()) {
                $li.data('hoverLocked', true);
            }
        } else {
            $li.removeData('hoverLocked');
            openToggle($toggle);
        }
    });

    $menu.on(`mouseenter${EVENT_NS}`, 'li.menu__item--has-children', function onHoverOpen() {
        if (!canHoverOpen()) {
            return;
        }

        const $li = $(this);
        clearHoverTimer();

        if ($li.data('hoverLocked') || otherKeyboardOpenHasFocus($li)) {
            return;
        }

        scheduleHover(() => {
            if ($li.data('hoverLocked') || otherKeyboardOpenHasFocus($li)) {
                return;
            }

            const $toggle = getToggleForLi($li);
            if (!$toggle.length) {
                return;
            }

            closeSiblings($toggle);
            openToggle($toggle);
        });
    });

    $menu.on(`mouseleave${EVENT_NS}`, 'li.menu__item--has-children', function onHoverClose() {
        if (!canHoverOpen()) {
            return;
        }

        const $li = $(this);
        clearHoverTimer();
        $li.removeData('hoverLocked');

        scheduleHover(() => {
            if (shouldKeepOpenForKeyboard($li)) {
                return;
            }

            const $toggle = getToggleForLi($li);
            if ($toggle.length) {
                closeToggle($toggle);
            }
        });
    });

    $(document).on(`pointerdown${EVENT_NS}`, () => {
        pointerActive = true;
    });

    $(document).on(`keydown${EVENT_NS}`, (e) => {
        if (KEYBOARD_NAV_KEYS[e.key]) {
            pointerActive = false;
        }

        if (e.key !== 'Escape') {
            return;
        }

        const $active = $(document.activeElement);
        const $insideLi = $active.closest('li.menu__item--has-children');

        if ($insideLi.length) {
            const $toggle = getToggleForLi($insideLi);
            if ($toggle.length && isOpen($toggle)) {
                closeToggle($toggle);
                $toggle.trigger('focus');
                return;
            }
        }

        closeAll($menu);
    });

    $(document).on(`click${EVENT_NS}`, (e) => {
        if (!isDesktop()) {
            return;
        }

        const $target = $(e.target);

        if ($target.closest(TOGGLE_SELECTOR).length) {
            return;
        }

        const $openItem = $target.closest('li.menu__item--has-children');
        if ($openItem.children(`${TOGGLE_SELECTOR}[aria-expanded="true"]`).length) {
            return;
        }

        closeAll($menu);
    });

    $(document).on(`focusin${EVENT_NS}`, (e) => {
        if (!isDesktop()) {
            return;
        }

        const $target = $(e.target);

        if ($target.closest(TOGGLE_SELECTOR).length) {
            return;
        }

        const $openItem = $target.closest('li.menu__item--has-children');
        if ($openItem.children(`${TOGGLE_SELECTOR}[aria-expanded="true"]`).length) {
            return;
        }

        closeAll($menu);
    });

    $(window).on(`resize${EVENT_NS}`, () => {
        $menu.find(`${TOGGLE_SELECTOR}[aria-expanded="true"]`).each(function refitOpen() {
            const $submenu = getSubmenu($(this));
            if ($submenu.length) {
                fitSubmenu($submenu);
            }
        });
    });
};

export default initDropdownMenu;
